import { 
  Timeline, 
  TimelineItem, 
  TimelineSeparator, 
  TimelineConnector, 
  TimelineContent, 
  TimelineDot,
  timelineItemClasses
} from '@mui/lab';
import Tooltip from "@mui/material/Tooltip";
import { 
  TrendingUpDown, 
  Target, 
  MessagesSquare, 
  ShieldCheck, 
  Hospital, 
  TriangleAlert, 
  ChartGantt, 
  User,
  CheckSquare,
  Activity,
  Users,
  Shield,
  Info,
Globe} from 'lucide-react';
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import SafeHtml from '../components/common/SafeHtml';

// Centralized severity color functions
const getSeverityColor = (severityRank) => {
  if (!severityRank || severityRank < 1 || severityRank > 5) return 'var(--color-medium-gray)';
  
  switch (severityRank) {
    case 1: return 'var(--color-light-green)'; 
    case 2: return 'var(--color-medium-purple)'; 
    case 3: return 'var(--color-dark-purple)'; 
    case 4: return 'var(--color-medium-orange)'; 
    case 5: return 'var(--color-medium-red)'; 
    default: return 'var(--color-medium-purple)';
  }
};

const getSeverityColorFromKeyword = (progress) => {
  if (!progress || typeof progress !== 'string') return 'var(--color-medium-gray)';
  
  const progressLower = progress.toLowerCase();
  
  // Positive/completion indicators - equivalent to severity 1-2
  if (progressLower.includes('complet') || 
      progressLower.includes('finish') || 
      progressLower.includes('success') ||
      progressLower.includes('good') ||
      progressLower.includes('excellent') ||
      progressLower.includes('improv') ||
      progressLower.includes('resolved')) {
    return getSeverityColor(1); // Green
  }
  
  // Active/ongoing indicators - equivalent to severity 2-3
  if (progressLower.includes('ongoing') || 
      progressLower.includes('active') || 
      progressLower.includes('current') ||
      progressLower.includes('stable') ||
      progressLower.includes('continuing') ||
      progressLower.includes('progress')) {
    return getSeverityColor(2); // Light blue
  }
  
  // Warning/variable indicators - equivalent to severity 3
  if (progressLower.includes('variable') || 
      progressLower.includes('mixed') || 
      progressLower.includes('partial') ||
      progressLower.includes('limited') ||
      progressLower.includes('moderate')) {
    return getSeverityColor(3); // Yellow
  }
  
  // Concerning indicators - equivalent to severity 4
  if (progressLower.includes('poor') || 
      progressLower.includes('declining') || 
      progressLower.includes('worsening')) {
    return getSeverityColor(4); // Orange
  }
  
  // Critical indicators - equivalent to severity 5
  if (progressLower.includes('failed') ||
      progressLower.includes('discontinued') ||
      progressLower.includes('crisis') ||
      progressLower.includes('emergency')) {
    return getSeverityColor(5); // Red
  }
  
  // Default neutral color
  return 'var(--color-medium-gray)';
};

const PatientOverview = ({ patientData, historySummary, overallSeverityData, settings, customSectionOrder }) => {
  const { flags } = useFeatureFlags();

  // Default order configuration
  const defaultSectionOrder = [
    'demographics',
    'severity',
    'treatmentGoals',
    'talkingPoints',
    'actionItems',
    'clinicalPicture',
    'strengths',
    'keySupports',
    'careTeam',
    'careTimeline',
    'riskFactors',
    'safety'
  ];



  const formatSeverityTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return 'var(--color-success-text)';
      case 'worsening': return 'var(--color-error-text)';
      default: return 'var(--color-medium-gray)';
    }
  };

  // Simple line chart component
  const SimpleLineChart = ({ data, width = 280, height = 120 }) => {
    if (!data || data.length === 0) {
      return (
        <div className="chart-no-data">
          No chart data available
        </div>
      );
    }

    // Group data by month and calculate averages
    const monthlyData = {};
    data.forEach(item => {
      const date = new Date(item.originalDate || item.date);
      const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          date: date,
          severities: [],
          monthLetter: date.toLocaleDateString('en-US', { month: 'narrow', timeZone: 'UTC' }),
          monthShort: date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
        };
      }
      monthlyData[monthKey].severities.push(item.severity);
    });

    // Find the date range of actual data
    const dataMonthKeys = Object.keys(monthlyData).sort();
    if (dataMonthKeys.length === 0) {
      return (
        <div className="chart-no-data">
          No chart data available
        </div>
      );
    }

    const firstDataMonth = dataMonthKeys[0];
    const lastDataMonth = dataMonthKeys[dataMonthKeys.length - 1];
    
    // Parse first and last month dates
    const [firstYear, firstMonth] = firstDataMonth.split('-').map(Number);
    const [lastYear, lastMonth] = lastDataMonth.split('-').map(Number);
    
    const startDate = new Date(firstYear, firstMonth - 1, 1);
    const endDate = new Date(lastYear, lastMonth - 1, 1);

    // Create timeline only between first and last data points
    const completeTimeline = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const monthData = monthlyData[monthKey];
      
      completeTimeline.push({
        date: new Date(currentDate),
        monthKey: monthKey,
        monthLetter: currentDate.toLocaleDateString('en-US', { month: 'narrow' }),
        monthShort: currentDate.toLocaleDateString('en-US', { month: 'short' }),
        severity: monthData ? 
          monthData.severities.reduce((sum, val) => sum + val, 0) / monthData.severities.length : 
          null,
        hasData: !!monthData
      });
      
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    const padding = 20;
    const chartWidth = width - (padding * 2);
    const chartHeight = height - (padding * 2);
    
    const maxY = 5;
    const minY = 1;
    const yRange = maxY - minY;
    
    const xStep = chartWidth / (completeTimeline.length - 1);
    
    // Determine if we have space for 3-letter month names
    // Estimate ~20px per 3-letter abbreviation vs ~8px for single letter
    const estimatedSpacePerLabel = 20;
    const maxLabelsFor3Letter = Math.floor(chartWidth / estimatedSpacePerLabel);
    const use3LetterAbbrev = completeTimeline.length <= maxLabelsFor3Letter;
    
    // Create points for all months
    const allPoints = completeTimeline.map((point, index) => {
      const x = padding + (index * xStep);
      return { 
        x, 
        monthLetter: point.monthLetter,
        monthShort: point.monthShort,
        hasData: point.hasData,
        severity: point.severity,
        y: point.severity ? padding + chartHeight - ((point.severity - minY) / yRange * chartHeight) : null
      };
    });

    // Create path data only for points with data
    const dataPoints = allPoints.filter(p => p.hasData && p.severity !== null);
    const pathData = dataPoints.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ');

    return (
      <div className="chart-container">
        <svg width="100%" height={height} className="chart-svg">
          {/* Grid lines */}
          {[1, 2, 3, 4, 5].map(value => {
            const y = padding + chartHeight - ((value - minY) / yRange * chartHeight);
            return (
              <g key={value}>
                <line
                  x1={padding}
                  y1={y}
                  x2={width - padding}
                  y2={y}
                  stroke="var(--color-neutral-medium)"
                  strokeWidth="1"
                />
                <text
                  x={padding - 5}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="10"
                  fill="var(--color-neutral-dark)"
                >
                  {value}
                </text>
              </g>
            );
          })}
          
          {/* Main line - only for data points */}
          {dataPoints.length > 1 && (
            <path
              d={pathData}
              fill="none"
              stroke="var(--color-medium-blue)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          
          {/* Data points - only show circles for months with data */}
          {dataPoints.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="3"
              fill="var(--color-medium-blue)"
              stroke="var(--color-white)"
              strokeWidth="2"
            />
          ))}
          
          {/* X-axis labels */}
          {allPoints.map((point, index) => {
            // Adjust label frequency based on timeline length
            const labelFrequency = use3LetterAbbrev ? 
              Math.max(1, Math.ceil(allPoints.length / 8)) : 
              Math.max(1, Math.ceil(allPoints.length / 6));
            
            const showLabel = index === 0 || 
                             index === allPoints.length - 1 || 
                             index % labelFrequency === 0;
            
            if (showLabel) {
              const displayText = point.monthShort; //use3LetterAbbrev ? point.monthShort : point.monthLetter;
              return (
                <text
                  key={index}
                  x={point.x}
                  y={height - 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill={point.hasData ? "var(--color-light-blue-text)" : "var(--color-neutral-dark)"}
                  fontWeight={point.hasData ? "600" : "400"}
                >
                  {displayText}
                </text>
              );
            }
            return null;
          })}
        </svg>
      </div>
    );
  };

  // Helper function to find summary by originalName
  const findSummaryByOriginalName = (originalName) => {
    if (!historySummary?.history_summaries) return null;
    
    const summaries = historySummary.history_summaries;
    for (const key in summaries) {
      if (summaries[key].originalName === originalName) {
        return summaries[key];
      }
    }
    return null;
  };

  // Check if a summary value is meaningfully empty
  const isSummaryEmpty = (summary) => {
    if (!summary) return true;
    if (Array.isArray(summary)) return summary.length === 0;
    if (typeof summary === 'string') {
      const trimmed = summary.trim();
      return trimmed === '' || trimmed === '[]' || trimmed === '{}' || trimmed === 'null';
    }
    return false;
  };

  const renderDemographicsSection = () => {
    const demographics = findSummaryByOriginalName('demographics');
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <Globe className="patient-overview-icon" />
          Demographics
        </h3>
        <div className="patient-overview-box-content">
          <p>
            <strong>Date of Birth:</strong> {patientData.dateOfBirth}
          </p>
          <p>
            <strong>Gender:</strong> {patientData.gender}
          </p>
          {demographics?.summary && (
            <p className="demographics-content">
              {demographics.summary}
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderSeveritySection = () => {
    if (!overallSeverityData || overallSeverityData.overallSeverityRank === null || !flags?.showSeverity) {
      return null;
    }

    let chartData = [];
    const dailyData = overallSeverityData.dailyAverages;
    
    if (dailyData && Array.isArray(dailyData) && dailyData.length > 0) {
      chartData = dailyData.map(item => ({
        originalDate: item.date,
        severity: item.averageSeverity || item.average_severity || item.severity
      }));
    }

    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <div className="severity-section-header">
            <TrendingUpDown className="patient-overview-icon" />
            Overall Severity Ranking
          </div>
          <div className="severity-info-trigger">
            <Tooltip title={<SeverityTooltip />} arrow  classes={{ tooltip: 'custom-severity-tooltip' }}>
              <Info size={12} className="severity-info-icon" />
            </Tooltip>
          </div>
        </h3>
        <div className="patient-overview-box-content">
          <div className="severity-display">
            <span className={`severity-rank-display severity-color-${overallSeverityData.overallSeverityRank}`}>
              {overallSeverityData.overallSeverityRank}/5
            </span>
            <span className={`severity-trend-display trend-${overallSeverityData.overallSeverityTrend}`}>
              {overallSeverityData.overallSeverityTrend}
            </span>
          </div> 
          <div className="severity-chart-container">
            <SimpleLineChart data={chartData} />
          </div>
          {overallSeverityData.dateRange && (
            <div className="severity-date-range">
              {overallSeverityData.totalItems} AI Severity Rankings<br />from {overallSeverityData.dateRange.start.toLocaleDateString()} to {overallSeverityData.dateRange.end.toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTreatmentGoalsSection = () => {
    const treatmentGoals = findSummaryByOriginalName('treatment_goals');
    if (!treatmentGoals?.summary || isSummaryEmpty(treatmentGoals.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <Target className="patient-overview-icon" />
          Treatment Goals
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={treatmentGoals.summary} />
        </div>
      </div>
    );
  };

  const renderTalkingPointsSection = () => {
    const talkingPoints = findSummaryByOriginalName('talking_points');
    if (!talkingPoints?.summary || isSummaryEmpty(talkingPoints.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <MessagesSquare className="patient-overview-icon" />
          Talking Points
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={talkingPoints.summary} />
        </div>
      </div>
    );
  };

  const renderActionItemsSection = () => {
    const actionItems = findSummaryByOriginalName('patient_action_items');
    if (!actionItems?.summary || isSummaryEmpty(actionItems.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <CheckSquare className="patient-overview-icon" />
          Action Items
        </h3>
        <div className="patient-overview-box-content patient-overview-action-items">
          <SafeHtml html={actionItems.summary} />
        </div>
      </div>
    );
  };

  const renderClinicalPictureSection = () => {
    const clinicalPicture = findSummaryByOriginalName('clinical_picture');
    if (!clinicalPicture?.summary || isSummaryEmpty(clinicalPicture.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <Activity className="patient-overview-icon" />
          Clinical Picture
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={clinicalPicture.summary} />
        </div>
      </div>
    );
  };

  const renderStrengthsSection = () => {
    const strengths = findSummaryByOriginalName('strengths');
    if (!strengths?.summary || isSummaryEmpty(strengths.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <ShieldCheck className="patient-overview-icon" />
          Strengths
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={strengths.summary} />
        </div>
      </div>
    );
  };

  const renderKeySupportsSection = () => {
    const keySupports = findSummaryByOriginalName('key_supports');
    if (!keySupports?.summary || isSummaryEmpty(keySupports.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <Users className="patient-overview-icon" />
          Key Supports
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={keySupports.summary} />
        </div>
      </div>
    );
  };

  const renderCareTeamSection = () => {
    const careTeam = findSummaryByOriginalName('care_team');
    if (!careTeam?.summary || isSummaryEmpty(careTeam.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <Hospital className="patient-overview-icon" />
          Care Team
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={careTeam.summary} />
        </div>
      </div>
    );
  };

  const renderCareTimelineSection = () => {
    const careTimeline = findSummaryByOriginalName('care_timeline');
    if (!careTimeline?.summary || !Array.isArray(careTimeline.summary) || careTimeline.summary.length === 0) return null;
    
    // Sort timeline items by date (most recent first)
    const sortedItems = [...careTimeline.summary].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    
    // Use centralized severity color functions
    const getTimelineDotColor = (item) => {
      // If severity rank is available, use severity colors
      if (item.severityRank && item.severityRank >= 1 && item.severityRank <= 5) {
        return getSeverityColor(item.severityRank);
      }
      
      // Fallback to keyword-based colors
      return getSeverityColorFromKeyword(item.progress);
    };
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <ChartGantt className="patient-overview-icon" />
          Care Timeline
        </h3>
        <div className="patient-overview-box-content">
          <Timeline 
            className="care-timeline"
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {sortedItems.map((item, index) => (
              <TimelineItem key={index} className="care-timeline-item">
                <TimelineSeparator>
                  <TimelineDot 
                    style={{ 
                      backgroundColor: getTimelineDotColor(item),
                      width: '10px',
                      height: '10px'
                    }} 
                  />
                  {index < sortedItems.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent className="care-timeline-content">
                  <div className="care-timeline-date">
                    {new Date(item.date).toLocaleDateString()}
                  </div>
                  <div className="care-timeline-treatment">
                    {item.treatment}
                    {flags?.showSeverity && item.severityRank && (
                      <span className={`severity-color-${item.severityRank}`} style={{ 
                        marginLeft: '8px',
                        fontSize: 'var(--text-2xs)',
                        fontWeight: 'bold'
                      }}>
                        {item.severityRank}/5
                      </span>
                    )}
                  </div>
                  <div className="care-timeline-meta">
                    <span 
                      className="care-timeline-progress"
                      style={{ color: getTimelineDotColor(item) }}
                    >
                      {item.progress}
                    </span>
                  </div>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </div>
      </div>
    );
  };
  
  const renderRiskFactorsSection = () => {
    const riskFactors = findSummaryByOriginalName('risk_factors');
    if (!riskFactors?.summary || isSummaryEmpty(riskFactors.summary)) return null;
    
    return (
      <div className="patient-overview-box">
        <h3 className="patient-overview-box-title">
          <TriangleAlert className="patient-overview-icon" />
          Risk Factors
        </h3>
        <div className="patient-overview-box-content">
          <SafeHtml html={riskFactors.summary} />
        </div>
      </div>
    );
  };

const renderSafetySection = () => {
    const safety = findSummaryByOriginalName('safety');
    if (!safety?.summary || isSummaryEmpty(safety.summary)) return null;
    
    // Handle both old HTML string format and new object array format
    if (typeof safety.summary === 'string') {
      return (
        <div className="patient-overview-box">
          <h3 className="patient-overview-box-title">
            <Shield className="patient-overview-icon" />
            Safety Issues
          </h3>
          <div className="patient-overview-box-content">
            <SafeHtml html={safety.summary} />
          </div>
        </div>
      );
    }
    
    // Handle new object array format
    if (Array.isArray(safety.summary)) {
      // Sort by status (Recent first, then Past)
      const sortedConcerns = [...safety.summary].sort((a, b) => {
        const statusOrder = { 'Recent': 0, 'Past': 1 };
        const aOrder = statusOrder[a.status] ?? 2;
        const bOrder = statusOrder[b.status] ?? 2;
        return aOrder - bOrder;
      });
      
      return (
        <div className="patient-overview-box">
          <h3 className="patient-overview-box-title">
            <div className="safety-section-header">
              <Shield className="patient-overview-icon" />
                Safety Issues
            </div>
            {flags?.showSeverity && (
              <div className="severity-info-trigger">
                <Tooltip title={<SeverityTooltip />} arrow  classes={{ tooltip: 'custom-severity-tooltip' }}>
                  <Info size={12} className="severity-info-icon" />
                </Tooltip>
              </div>
            )}
          </h3>
          <div className="patient-overview-box-content">
            {sortedConcerns.map((item, index) => (
              <div key={index} className="safety-concern-item">
                <div className="safety-concern-header">
                  {flags?.showSeverity && (
                    <span className={`safety-concern-severity severity-color-${item.severityRank}`}>
                      {item.severityRank}/5
                    </span>
                  )}
                  <span className="safety-concern-status">
                    {item.status}
                  </span>
                </div>
                <div className="safety-concern-text">
                  {item.concern}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Severity ranking tooltip content
  const SeverityTooltip = () => (
    <div>
      <div className="tooltip-title">
        Severity Rankings
      </div>
      <div className="tooltip-content">
        <div className="tooltip-row">
          <span className="tooltip-color-indicator severity-bg-1"></span>
          <span><span className="tooltip-text-bold">1 - Minimal:</span> Low distress, minimal impact. Routine monitoring.</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-color-indicator severity-bg-2"></span>
          <span><span className="tooltip-text-bold">2 - Mild:</span> Manageable distress, minor impairment. Conscious effort needed.</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-color-indicator severity-bg-3"></span>
          <span><span className="tooltip-text-bold">3 - Moderate:</span> Significant distress, clear interference. Active intervention required.</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-color-indicator severity-bg-4"></span>
          <span><span className="tooltip-text-bold">4 - High:</span> High distress, substantial impairment. Increased care needed.</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-color-indicator severity-bg-5"></span>
          <span><span className="tooltip-text-bold">5 - Severe:</span> Overwhelming distress/crisis. Immediate intervention required.</span>
        </div>
      </div>
    </div>
  );

  // Section configuration mapping - moved after render functions
  const sectionConfig = {
    demographics: {
      key: 'demographics',
      render: renderDemographicsSection,
      alwaysShow: true
    },
    severity: {
      key: 'severity',
      render: renderSeveritySection,
      alwaysShow: false
    },
    treatmentGoals: {
      key: 'treatmentGoals',
      render: renderTreatmentGoalsSection,
      alwaysShow: false
    },
    talkingPoints: {
      key: 'talkingPoints',
      render: renderTalkingPointsSection,
      alwaysShow: false
    },
    actionItems: {
      key: 'actionItems',
      render: renderActionItemsSection,
      alwaysShow: false
    },
    clinicalPicture: {
      key: 'clinicalPicture',
      render: renderClinicalPictureSection,
      alwaysShow: false
    },
    strengths: {
      key: 'strengths',
      render: renderStrengthsSection,
      alwaysShow: false
    },
    keySupports: {
      key: 'keySupports',
      render: renderKeySupportsSection,
      alwaysShow: false
    },
    careTeam: {
      key: 'careTeam',
      render: renderCareTeamSection,
      alwaysShow: false
    },
    careTimeline: {
      key: 'careTimeline',
      render: renderCareTimelineSection,
      alwaysShow: false
    },
    riskFactors: {
      key: 'riskFactors',
      render: renderRiskFactorsSection,
      alwaysShow: false
    },
    safety: {
      key: 'safety',
      render: renderSafetySection,
      alwaysShow: false
    }
  };

  // Get current section order
  const getCurrentSectionOrder = () => {
    // Use custom order if provided, otherwise fall back to default
    if (customSectionOrder && Array.isArray(customSectionOrder) && customSectionOrder.length > 0) {
      return customSectionOrder;
    }
    return defaultSectionOrder;
  };
  // Generate sections dynamically based on order
  const generateSections = () => {
    const currentOrder = getCurrentSectionOrder();
    const sections = [];

    currentOrder.forEach(sectionKey => {
      const config = sectionConfig[sectionKey];
      if (config) {
        const sectionElement = config.render();
        // Only add section if it returns content (not null)
        if (sectionElement) {
          sections.push({
            key: sectionKey,
            element: sectionElement
          });
        }
      }
    });

    return sections;
  };

  if (!patientData) {
    return <div>Loading patient data...</div>;
  }

  const sections = generateSections();

  return (
    <div className="patient-details-container userback-ignore">
      <div className="patient-overview-grid">
        {sections.map(section => (
          <div key={section.key}>
            {section.element}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PatientOverview;
